"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { XIcon } from "@/components/ui/XIcon";
import { useLoginRequired } from "@/hooks/auth";
import { useMediaQuery } from "@/hooks/use-media-query";
import type { components } from "@/types/api";
import { NFTCardPurchaseContent } from "./NFTCardPurchaseContent";

interface NFTCardDrawerProps {
  nftData: components["schemas"]["AvailableNftResponseDto"];
  nftMarketAddress: string;
  nftCollectionAddress: string;
  isTicketEnabled?: boolean;
  marketTypeId?: string;
}

export const NFTCardDrawer = ({
  nftData,
  nftMarketAddress,
  nftCollectionAddress,
  isTicketEnabled,
  marketTypeId,
}: NFTCardDrawerProps) => {
  const t = useTranslations("nft");
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const handleLoginRequired = useLoginRequired();

  const purchaseContent = (
    <NFTCardPurchaseContent
      nftData={nftData}
      nftMarketAddress={nftMarketAddress}
      nftCollectionAddress={nftCollectionAddress}
      isTicketEnabled={isTicketEnabled}
      marketTypeId={marketTypeId}
      onRedirect={() => setIsOpen(false)}
    />
  );

  // Desktop: Dialog
  if (isDesktop) {
    return (
      <>
        <Button
          colors={"primary"}
          className="w-full"
          onClick={handleLoginRequired(() => {
            setIsOpen(true);
          })}
        >
          {t("drawer.buy")}
        </Button>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-xs space-y-4 px-9">
            <DialogHeader className="flex-row items-center justify-between">
              <DialogTitle className="text-right m-0 mt-0.5">
                {t("drawer.invoice")}
              </DialogTitle>
              <DialogClose asChild>
                <Button size={"icon"} variant={"light"} className="p-0">
                  <XIcon size={20} />
                </Button>
              </DialogClose>
            </DialogHeader>
            <DialogDescription asChild>{purchaseContent}</DialogDescription>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Button
        colors={"primary"}
        className="w-full"
        onClick={handleLoginRequired(() => {
          setIsOpen(true);
        })}
      >
        {t("drawer.buy")}
      </Button>
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent>
          <DrawerHeader className="flex-row items-center justify-between px-0 pb-0">
            <DrawerTitle className="text-right m-0 mb-4">
              {t("drawer.invoice")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerDescription asChild className="px-0">
            {purchaseContent}
          </DrawerDescription>
        </DrawerContent>
      </Drawer>
    </>
  );
};
